import React, {useEffect, useMemo, useState} from 'react';
import {Divider, Spin, Table, Typography} from 'antd';
import {ColumnsType} from 'antd/es/table';
import {ExUserRegistrationDetails, ModelAccessRequest, SubModelAccessRequest, SubModelObject} from 'hemwb-api';
import {requestExternalUserDetails} from '../../../../../../store/user';
import {CircularProgress} from '@mui/material';
import {TO_LOCALE_DATE_STRING_OPTIONS} from '../../../../../core/TableWithSortAndPagination';
import {getAccessType, AccessRoleKey} from '../../../../model/common/ModelRequestAccess/ModelAccessConfirmationDialog';
import {getSubModelAccessType} from '../../enroll/utils';
import {Link} from 'react-router-dom';
import {getUrlRoute, MRAppRoutes} from '../../../../../router/routes';
import styles from './UserDetails.module.scss';

export type UserActionMenuProps = {
  selectedUser: ExUserRegistrationDetails | null;
};

//type DropdownKey = 'registrationInfo' | 'modelEntries' | 'activityHistory';

const UserDetails: React.FC<UserActionMenuProps> = ({selectedUser}) => {
  const [userDetail, setUserDetail] = useState<ModelAccessRequest[]>([]);
  const [userDetailLoaded, setUserDetailLoaded] = useState<boolean>(false);
  const [userSubModelDetail, setUserSubModelDetail] = useState<SubModelAccessRequest[]>([]);
  const [userHistoryDetail, setUserHistoryDetail] = useState<ModelAccessRequest[]>([]);
  const [subModelHistoryDetail, setSubModelHistoryDetail] = useState<SubModelAccessRequest[]>([]);
  const [activationDate, setActivationDate] = useState<string>();
  const [deactivationDate, setDeactivationDate] = useState<string>();
  const {Text, Title} = Typography;

  type FlatSubModelRow = SubModelObject & {modelName: string; modelUuid: string};

  const modelColumns: ColumnsType<ModelAccessRequest> = [
    {
      title: 'Model Name',
      dataIndex: 'modelName',
      key: 'modelName',
      //ellipsis: true,
      render: (_value, record) => {
        const displayName =
          record.modelName && record.modelName.length > 60
            ? `${record.modelName.substring(0, 60)}...`
            : record.modelName;

        // Show as hyperlink for active, pending registration, and pending review statuses
        const shouldShowLink =
          selectedUser && ['ACTIVE', 'PENDING_REGISTRATION', 'PENDING_REVIEW'].includes(selectedUser.status);

        if (shouldShowLink && record.modelUuid) {
          return (
            <Link to={getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: record.modelUuid})} style={{color: '#0a5ca3'}}>
              {displayName}
            </Link>
          );
        }

        return displayName;
      },
      sorter: (a, b) => String(a.modelName || '').localeCompare(String(b.modelName || '')),
    },
    {
      title: 'Assigned Model Role',
      dataIndex: 'modelRole',
      key: 'modelRole',
      render: (modelRole: AccessRoleKey) => getAccessType(modelRole),
      sorter: (a, b) => {
        const roleA = getAccessType((a.modelRole as unknown as AccessRoleKey) ?? '');
        const roleB = getAccessType((b.modelRole as unknown as AccessRoleKey) ?? '');
        return roleA.localeCompare(roleB);
      },
    },
  ];

  const subModelColumns: ColumnsType<FlatSubModelRow> = [
    {
      title: 'Sub Model Type',
      dataIndex: 'submodelType',
      key: 'submodelType',
      render: (subModelType) => getSubModelAccessType(subModelType),
      sorter: (a, b) =>
        getSubModelAccessType(a.submodelType || '').localeCompare(getSubModelAccessType(b.submodelType || '')),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => String(a.name || '').localeCompare(String(b.name || '')),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      ellipsis: true,
      sorter: (a, b) => String(a.createdBy || '').localeCompare(String(b.createdBy || '')),
    },
    {
      title: 'Modified On',
      dataIndex: 'modifiedOn',
      key: 'modifiedOn',
      render: (modifiedOn) =>
        modifiedOn ? new Date(modifiedOn).toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS) : '-',
      sorter: (a, b) => {
        const dateA = a.modifiedOn ? new Date(a.modifiedOn).getTime() : 0;
        const dateB = b.modifiedOn ? new Date(b.modifiedOn).getTime() : 0;
        return dateA - dateB;
      },
    },
  ];

  const assignedSubModels = useMemo(
    () =>
      userSubModelDetail.flatMap((group) =>
        (group.submodels || []).map((sub) => ({
          ...sub,
          modelName: group.modelName,
          modelUuid: group.modelUuid,
        })),
      ),
    [userSubModelDetail],
  );

  const historySubModels = useMemo(
    () =>
      subModelHistoryDetail.flatMap((group) =>
        (group.submodels || []).map((sub) => ({
          ...sub,
          modelName: group.modelName,
          modelUuid: group.modelUuid,
        })),
      ),
    [subModelHistoryDetail],
  );

  useEffect(() => {
    if (selectedUser) {
      requestExternalUserDetails(selectedUser.email).then((response) => {
        setUserDetailLoaded(true);
        setUserDetail(response.modelAccesses);
        setUserSubModelDetail(response.subModelAccessRequests || []);
        setUserHistoryDetail(response.modelAccessesHistory || []);
        setSubModelHistoryDetail(response.subModelAccessHistory || []);
        setActivationDate(
          response.activationDate
            ? response.activationDate.toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS)
            : 'N/A',
        );
        setDeactivationDate(
          response.deactivationDate
            ? response.deactivationDate.toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS)
            : 'N/A',
        );
      });
    }
  }, [selectedUser]);

  return (
    <>
      {!userDetailLoaded ? (
        <>
          <div className={styles.loadingContainer}>
            <CircularProgress />
          </div>
        </>
      ) : (
        <>
          <div className={styles.container}>
            <Title level={4} className={styles.mainTitle}>
              User Overview
            </Title>
            <Text className={styles.description}>
              Displays key information about the external user. Includes registration info, access permissions, and
              activity history.
            </Text>
          </div>

          <Spin spinning={false}>
            <div className={styles.section}>
              <Title level={5} className={styles.sectionTitle}>
                Registration info
              </Title>
              <div className={styles.contentText}>
                <div>
                  <Text strong>Registered By:</Text> <Text>{selectedUser?.createdBy || 'N/A'}</Text>
                </div>
                <div>
                  <Text strong>Activation Date:</Text> <Text>{activationDate || 'N/A'}</Text>
                </div>
                <div>
                  <Text strong>Deactivation Date:</Text> <Text>{deactivationDate || 'N/A'}</Text>
                </div>
              </div>
            </div>

            <Divider className={styles.divider} />

            <div>
              <Title level={5} className={styles.sectionTitle}>
                Model Entries
              </Title>
              <Table<ModelAccessRequest>
                rowKey={(record) => record.modelUuid}
                size="small"
                pagination={false}
                columns={modelColumns}
                dataSource={userDetail}
                locale={{emptyText: 'No model entries assigned'}}
              />
            </div>

            <div className={styles.sectionMarginTop}>
              <Title level={5} className={styles.sectionTitle}>
                Sub-models and Roles Assigned
              </Title>
              <Table<FlatSubModelRow>
                rowKey={(record) => `${record.modelUuid}-${record.submodelId}`}
                size="small"
                pagination={false}
                columns={subModelColumns}
                dataSource={assignedSubModels}
                locale={{emptyText: 'No sub-model access assigned'}}
              />
            </div>

            <div>
              <Title level={5} className={styles.sectionTitle}>
                Activity History
              </Title>
              <Table<ModelAccessRequest>
                rowKey={(record) => `${record.modelUuid}-${record.modelRole}`}
                size="small"
                pagination={false}
                columns={modelColumns}
                dataSource={userHistoryDetail}
                locale={{emptyText: 'No activity history found'}}
              />

              <Table<FlatSubModelRow>
                rowKey={(record) => `history-${record.modelUuid}-${record.submodelId}`}
                className={styles.historyTable}
                size="small"
                pagination={false}
                columns={subModelColumns}
                dataSource={historySubModels}
                locale={{emptyText: 'No sub-model activity history found'}}
              />
            </div>
          </Spin>
        </>
      )}
    </>
  );
};

export default UserDetails;
